import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useCallback,
  useRef,
  useState,
} from "react";

import classnames from "classnames";
import { FieldError } from "react-hook-form";
import { useWombatTheme } from "../../../theme";
import useCombinedRefs from "../../../utils/useCombinedRefs";

export type RadioVariant = "base" | "boxed";

type RadioProps = {
  /**
   * Label
   */
  label: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Input id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: radio_${id}_group
   *  - Label: radio_${id}_label
   *  - Input: radio_${id}
   *  - Help text: radio_${id}_help_message
   *  - Error message: radio_${id}_error_message
   *
   * It will also do the matching from the label to the input in respect to accessibility patterns
   */
  id: string;
  /**
   * Helper text
   * @default undefined
   */
  help?: string;
  /**
   * Variant
   * @default 'base'
   */
  variant?: RadioVariant;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
} & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "type"
>;

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      help,
      error = {},
      id,
      srOnlyLabel,
      variant = "base",
      disabled = false,
      onChange,
      className,
      ...props
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const innerRef = useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const [variantStyleStatus, setVariantStyleStatus] = useState(
      classnames(
        theme.form.radio.variant.container[variant],
        theme.form.radio.variant.state[variant][
          props.defaultChecked ? "checked" : "unchecked"
        ]
      )
    );

    const _onChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setVariantStyleStatus(
          classnames(
            theme.form.radio.variant.container[variant],
            theme.form.radio.variant.state[variant][
              combinedRef?.current?.checked ? "checked" : "unchecked"
            ]
          )
        );
        if (onChange) onChange(e);
      },
      [onChange, variant]
    );

    const radioInputVariantStyle = classnames(
      theme.form.radio.variant.input[variant],
    );
    const radioStyle = classnames(
      theme.form.radio.base,
      theme.form.radio.status[error?.message ? "error" : "default"],
      "cursor-pointer",
      className
    );
    const labelStyle = classnames(
      theme.form.radio.label.base,
      "cursor-pointer",
      disabled ? theme.form.radio.label.disabled : theme.form.radio.label.able,
      srOnlyLabel && "sr-only"
    );
    const helpStyle = classnames(
      theme.form.radio.help[error?.message ? "error" : "default"],
      srOnlyLabel && "sr-only"
    );

    return (
      <div className={variantStyleStatus} data-test-id={`radio_${id}_group`}>
        <div className={radioInputVariantStyle}>
          <input
            id={id}
            ref={combinedRef}
            disabled={disabled}
            aria-disabled={disabled ? "true" : "false"}
            type="radio"
            className={radioStyle}
            data-test-id={`radio_${id}`}
            onChange={_onChange}
            {...props}
          />
        </div>
        <label
          htmlFor={id}
          className={labelStyle}
          data-test-id={`radio_${id}_label`}
        >
          <div className={`ml-3 text-sm ${srOnlyLabel ? "sr-only" : ""}`}>
            {label}
            {(help || error?.message) && (
              <p
                className={helpStyle}
                data-test-id={`radio_${id}_${
                  error?.message ? "error_message" : "help_message"
                }`}
              >
                {error?.message ? error?.message : help}
              </p>
            )}
          </div>
        </label>
      </div>
    );
  }
);

export default React.memo(Radio);
